import {
  Box,
  Button,
  Center,
  Checkbox,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  type TextProps,
  Tooltip,
  useClipboard,
  useDisclosure,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type { BudgetStatusEnum, Maybe } from "@src/__generated__/graphql";
import type { BudgetsTableProps } from "@src/components/modules/budgets/export";
import { LinkIconOnHover } from "@src/components/modules/clients/listing/LinkIconOnHover";
import { formatDate, Link, ModalConfirm } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { BACK_BUTTON_QUERY_PARAM_KEY } from "@src/components/ui-kit/NavBar/BackButton";
import { cannot } from "@src/utils/components/permissions";
import { currency } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { capitalize } from "lodash";
import { observer } from "mobx-react-lite";
import { useRef } from "react";
import type { BudgetModel } from "../../projects/budgets/models/budget";

type Props = { row: BudgetModel };
export function BrandRenderer({ row }: Props) {
  return (
    <LinkIconOnHover dataComponentName="view-brand-text">
      <Link
        href={{ pathname: "/brands/detail", query: { id: row.brand?.id } }}
        target="_blank"
      >
        <Text display="flex" flexFlow="row">
          {row.brand?.name ?? row.temporaryBrand?.name ?? ""}
        </Text>
      </Link>
      &nbsp;
    </LinkIconOnHover>
  );
}

type ProjectRendererProps = {
  label: string | undefined | null;
  fontStyle?: TextProps["fontStyle"];
};
function ProjectRendererText({
  label,
  fontStyle = "normal",
}: ProjectRendererProps) {
  return (
    <Tooltip label={label} placement="top">
      <Text w="full" fontStyle={fontStyle} isTruncated>
        {label}
      </Text>
    </Tooltip>
  );
}
export function ProjectRenderer({ row }: Props) {
  return row.project?.id ? (
    <LinkIconOnHover dataComponentName="view-brand-text">
      <Link
        href={{
          pathname: "/projects/detail",
          query: { id: row.project?.id },
        }}
        target="_blank"
      >
        <ProjectRendererText label={row.project.title} />
      </Link>
      &nbsp;
    </LinkIconOnHover>
  ) : (
    <HStack minW="44" maxW="44" role="group">
      <ProjectRendererText
        label={row.temporaryProject?.name}
        fontStyle="italic"
      />
      {!row.project_id && row.brand?.name.toLowerCase() !== "templates" && (
        <Link
          href={{
            pathname: "/projects/create",
            query: {
              budget_id: row.id,
              brand_id: row.brand?.id ?? "",
              title: row.project?.title ?? row.temporaryProject?.name ?? "",
            },
          }}
        >
          <Button
            w="fit-content"
            colorScheme="violet"
            size="xs"
            variant="outline"
          >
            <HStack spacing="0">
              <Icon name="plus" boxSize="3" />
              <Text
                display="none"
                fontSize="inherit"
                fontWeight="inherit"
                _groupHover={{ display: "block", ml: "2" }}
              >
                <Trans>Project</Trans>
              </Text>
            </HStack>
          </Button>
        </Link>
      )}
    </HStack>
  );
}

type VersionRendererProps = {
  version: Maybe<number>;
};
export function VersionRenderer({ version }: VersionRendererProps) {
  return version;
}

type AccountManagerRendererProps = {
  accountManager: Maybe<string>;
};
export function AccountManagerRenderer({
  accountManager,
}: AccountManagerRendererProps) {
  const email = accountManager?.split(", ")?.[0] ?? "";
  return <Text>{email}</Text>;
}

type StatusRendererProps = {
  status: BudgetStatusEnum;
};
export function StatusRenderer({ status }: StatusRendererProps) {
  return capitalize(status.replace("_", " "));
}

type ApprovalRendererProps = {
  budget: BudgetModel;
};
export const ApprovalRenderer = observer(function ApprovalRenderer({
  budget,
}: ApprovalRendererProps) {
  return (
    <HStack justify="center" spacing="2">
      {budget.isActionsLoading ? (
        <Spinner size="sm" />
      ) : (
        <Checkbox
          isChecked={budget.internally_approved ?? false}
          isDisabled={budget.isActionsLoading}
          onChange={({ target }) => {
            budget.handleApproveInternal(target.checked);
          }}
        />
      )}
      {budget.isApproving.client.value ? (
        <Spinner size="sm" />
      ) : (
        <Checkbox
          isChecked={budget.client_approved ?? false}
          isDisabled={!budget.internally_approved || budget.isActionsLoading}
          onChange={({ target }) => {
            budget.handleApproveClient(target.checked);
          }}
        />
      )}
    </HStack>
  );
});

type AssignmentRendererProps = {
  budget: BudgetModel;
  onAssign: (id: string) => void;
};
export const AssignmentRenderer = observer(function AssignmentRenderer({
  budget,
  onAssign,
}: AssignmentRendererProps) {
  if (budget.isAssigning.value) return <Spinner size="xs" />;

  return (
    <HStack align="center">
      <Tooltip label={t`Assign to project`} placement="top" shouldWrapChildren>
        <Checkbox
          isChecked={budget.is_active ?? false}
          isDisabled={(budget.is_active ?? false) || budget.isActionsLoading}
          onChange={() => {
            onAssign(budget.id);
          }}
        />
      </Tooltip>
      {budget.is_active && (
        <Tooltip label={t`Unassign budget`} placement="top" shouldWrapChildren>
          <IconButton
            aria-label={t`Unassign`}
            colorScheme="grey"
            icon={
              <Center pos="relative">
                <Icon name="link-02" pos="absolute" />
                <Icon name="slash-divider" pos="absolute" />
              </Center>
            }
            isDisabled={budget.isActionsLoading}
            onClick={budget.handleUnassign}
            size="xs"
            variant="ghost"
          />
        </Tooltip>
      )}
    </HStack>
  );
});

type PriceRendererProps = {
  price: Maybe<number>;
};
export function PriceRenderer({ price }: PriceRendererProps) {
  return currency.formatByWorkspace(price ?? 0);
}

type DateRendererProps = {
  date: Date;
};
export function DateRenderer({ date }: DateRendererProps) {
  return <Text>{formatDate(date)}</Text>;
}

type QuoteRendererProps = {
  budget: BudgetModel;
};
export const QuoteRenderer = observer(function QuoteRenderer({
  budget,
}: QuoteRendererProps) {
  const { workspaceStore } = useStore();
  const quoteCount = budget.quotes?.length ?? 0;
  const backParam = `${location.pathname}${location.search}`;

  const createQuoteOnlyAfterApprovedBudgets =
    workspaceStore.settings?.createQuoteOnlyAfterApprovedBudgets ?? false;
  const isDisabled = createQuoteOnlyAfterApprovedBudgets
    ? cannot("quote_create_withoutInternalApproved") &&
      budget.internally_approved === false
    : false;

  return quoteCount > 0 ? (
    <HStack wrap="wrap" maxW="140px" spacing="0">
      {budget.quotes?.map((quote, index) => (
        <>
          <Link
            key={`budget-quote-${quote.id}`}
            target="_blank"
            href={{
              pathname: "/budgets/edit-quote",
              query: {
                id: quote.id,
                [BACK_BUTTON_QUERY_PARAM_KEY]: backParam,
              },
            }}
          >
            {quote.number}
          </Link>
          <Box
            w="0px"
            color="blue.500"
            opacity="0"
            _groupHover={{
              width: "4",
              opacity: "1",
              ml: "1",
            }}
            transition="all"
            transitionDuration="200ms"
          >
            <Tooltip label={t`Download PDF`} placement="top" shouldWrapChildren>
              <Link size="4" href={quote.url} target="_blank" isExternal>
                <Icon name="file-download-02" />
              </Link>
            </Tooltip>
          </Box>
          {index < quoteCount - 1 && <Text>,&nbsp;</Text>}
        </>
      ))}
    </HStack>
  ) : (
    <Box minW="24">
      <Button
        colorScheme="violet"
        isDisabled={isDisabled}
        isLoading={budget.isActionsLoading}
        onClick={() => {
          budget.createQuote({
            [BACK_BUTTON_QUERY_PARAM_KEY]: backParam,
          });
        }}
        size="xs"
        variant="outline"
      >
        <HStack spacing="0">
          <Icon name="plus" boxSize="3" />
          <Text
            display="none"
            fontSize="inherit"
            fontWeight="inherit"
            _groupHover={{ display: "block", ml: "2" }}
          >
            <Trans>Quote</Trans>
          </Text>
        </HStack>
      </Button>
    </Box>
  );
});

type ActionsRendererProps = Partial<BudgetsTableProps> & {
  isSubRow?: boolean;
  budget: BudgetModel;
  onDelete?: (id: string) => void;
  onCreateQuote?: (ids: string[]) => void;
  isDeleteDisabled?: boolean;
};
export const ActionsRenderer = observer(function ActionsRenderer({
  budget,
  isSubRow,
  returnParams,
  onDelete,
  projectId,
  isDeleteDisabled,
}: ActionsRendererProps) {
  const { UIStore, workspaceStore } = useStore();
  const { onCopy } = useClipboard(
    projectId
      ? `https://${location.hostname}/projects/budget/?id=${projectId}&budget_id=${budget.id}`
      : `https://${location.hostname}/budgets/proposals/?budget_id=${budget.id}`,
  );
  const downloadMenuItemRef = useRef<HTMLButtonElement>(null);
  const confirmDisclosure = useDisclosure();

  const languages = workspaceStore.settings?.budget_xlsx_languages ?? [];

  return (
    <>
      <HStack>
        <Menu>
          <MenuButton
            as={IconButton}
            colorScheme="gray"
            icon={<Icon name="dots-vertical" />}
            isLoading={budget.isActionsLoading}
            variant="ghost"
          />
          <MenuList>
            <Box
              // NOTE: Hack to add MenuItems bg color on hover, chakra actually assignes bg with focus
              // selectors and somehow for nested menus it doesn't work
              onMouseOver={() => {
                downloadMenuItemRef.current?.blur();
                setTimeout(() => {
                  downloadMenuItemRef.current?.focus();
                }, 0);
              }}
            >
              <MenuItem as={Menu} placement="left-start">
                <MenuButton w="full">
                  <MenuItem ref={downloadMenuItemRef} w="full">
                    {budget.isDownloading.value && <Spinner size="xs" />}
                    <Trans>Download</Trans>
                  </MenuItem>
                </MenuButton>
                <MenuList>
                  {budget.showDownloadPdf && (
                    <MenuItem
                      isDisabled={budget.isActionsLoading}
                      onClick={() => {
                        budget.createQuote({
                          [BACK_BUTTON_QUERY_PARAM_KEY]: `${location.pathname}${location.search}`,
                        });
                      }}
                    >
                      <Trans>Download PDF</Trans>
                    </MenuItem>
                  )}
                  {languages?.map((language) => (
                    <MenuItem
                      key={language}
                      isDisabled={budget.isActionsLoading}
                      onClick={() => {
                        budget.handleXlsxDownload(language);
                      }}
                    >
                      <Trans>Download XLSX -</Trans>&nbsp;
                      {language}
                    </MenuItem>
                  ))}
                </MenuList>
              </MenuItem>
            </Box>
            <MenuItem
              onClick={() => {
                onCopy();
                UIStore.toast({
                  title: t`Link copied successfully`,
                  status: "success",
                });
              }}
            >
              <Trans>Share</Trans>
            </MenuItem>
            {!isSubRow && (
              <MenuItem
                isDisabled={budget.isArchiveDisabled || budget.isActionsLoading}
                onClick={() => {
                  budget.handleArchive();
                  UIStore.toast({
                    title: t`Update successful`,
                    status: "success",
                  });
                }}
              >
                {budget.isArchiving.value && <Spinner size="xs" />}
                {budget.isArchived.value ? (
                  <Trans>Unarchive</Trans>
                ) : (
                  <Trans>Archive</Trans>
                )}
              </MenuItem>
            )}
            <MenuItem
              isDisabled={
                isDeleteDisabled ||
                budget.isDeleteDisabled ||
                budget.isActionsLoading
              }
              onClick={confirmDisclosure.onOpen}
            >
              {budget.isDeleting.value && <Spinner size="xs" />}
              {budget.isDeleteDisabled ? (
                <Trans>Assigned budget can't be removed</Trans>
              ) : (
                <Trans>Delete</Trans>
              )}
            </MenuItem>
          </MenuList>
        </Menu>
        <Link
          href={{
            pathname: projectId
              ? "/projects/budgets/edit"
              : "/budgets/proposals",
            query: {
              id: projectId,
              project_id: projectId,
              budget_id: budget.id,
              returnParams: returnParams,
              [BACK_BUTTON_QUERY_PARAM_KEY]:
                location.pathname + location.search,
            },
          }}
        >
          <Tooltip label={t`Edit`} placement="top">
            <IconButton
              aria-label={t`Edit budget`}
              colorScheme="gray"
              icon={<Icon name="edit-02" />}
              isLoading={budget.isActionsLoading}
              variant="ghost"
            />
          </Tooltip>
        </Link>
      </HStack>
      <ModalConfirm
        isOpen={confirmDisclosure.isOpen}
        destructive
        onConfirm={() => {
          onDelete?.(budget.id);
          confirmDisclosure.onClose();
        }}
        onClose={confirmDisclosure.onClose}
      />
    </>
  );
});
